import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * NoovaWidget — mounts our OWN chat widget on the public site (dogfooding).
 *
 * Behavior
 *  - Injects `noova-widget.js` exactly once, on the first public route it sees.
 *  - Loads on EVERY public page; hidden (not destroyed) while on /dashboard,
 *    so dashboard users don't get a second chat bubble over the admin UI.
 *  - No-ops during prerender and when credentials are absent, so a missing
 *    env var can never break the site build or a contributor's local run.
 *
 * Security note: the web API key is a *publishable* credential — it is visible
 * in the embed snippet of every site running this widget. It only authorizes
 * the widget chat endpoint and is rate-limited / origin-checked server-side.
 * Never put a privileged secret here; use VITE_NOOVA_WEB_KEY only.
 */

// Routes where the launcher should NOT appear. Empty by default (the widget
// shows on every public page). Add path prefixes here to suppress it, e.g.
// const EXCLUDED_PREFIXES = ["/login", "/signup", "/update-password"];
const EXCLUDED_PREFIXES: string[] = [];

const SCRIPT_ID = "noova-widget-script";
const HIDE_STYLE_ID = "noova-widget-hide-style";
const HIDE_CLASS = "noova-widget-hidden";

const env = import.meta.env;

const BOT_ID = (env.VITE_NOOVA_BOT_ID ?? "").trim();
const WEB_KEY = (env.VITE_NOOVA_WEB_KEY ?? "").trim();

// Falls back to the app's API base so local dev "just works".
const API_BASE = (
    env.VITE_NOOVA_WIDGET_API_BASE ??
    env.VITE_API_BASE_URL ??
    "http://localhost:8000"
).trim();

// Local file in dev, CDN in prod (same resolution the dashboard uses).
const SCRIPT_URL = (
    env.VITE_WIDGET_CDN_URL ??
    (env.DEV
        ? "/noova-widget.js"
        : "https://noovaspace.nyc3.cdn.digitaloceanspaces.com/widget/noova-widget.v1.min.js")
).trim();

// Presentation — override per environment without touching code.
const POSITION = (env.VITE_NOOVA_WIDGET_POSITION ?? "right").trim();
const SURFACE = (env.VITE_NOOVA_WIDGET_SURFACE ?? "glass").trim();
const COLOR_MODE = (env.VITE_NOOVA_WIDGET_COLOR_MODE ?? "light").trim();
const FAB_VARIANT = (env.VITE_NOOVA_WIDGET_FAB_VARIANT ?? "pill").trim();
const THEME_PRESET = (env.VITE_NOOVA_WIDGET_PRESET ?? "aurora").trim();

function isExcluded(pathname: string) {
    if (pathname.startsWith("/dashboard")) return true;
    return EXCLUDED_PREFIXES.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`),
    );
}

/**
 * Visibility is driven by a class on <html> plus a single stylesheet rule,
 * rather than by mutating the widget root directly. The widget script loads
 * asynchronously, so its root may not exist yet at the moment we want it
 * hidden — a CSS rule applies the instant the element appears, with no race.
 */
function ensureHideStyle() {
    if (document.getElementById(HIDE_STYLE_ID)) return;
    const st = document.createElement("style");
    st.id = HIDE_STYLE_ID;
    st.textContent = `
        .${HIDE_CLASS} .noova-root{display:none !important}
        .has-cookie-banner .noova-root { bottom: 124px !important; }
        @media (max-width: 768px) {
            .has-cookie-banner .noova-root { bottom: 230px !important; }
        }
    `;
    document.head.appendChild(st);
}

function setWidgetVisible(visible: boolean) {
    ensureHideStyle();
    document.documentElement.classList.toggle(HIDE_CLASS, !visible);
}

export default function NoovaWidget() {
    const { pathname } = useLocation();
    const hidden = isExcluded(pathname);

    useEffect(() => {
        // Never inject during prerender/SSR crawling.
        if (typeof window === "undefined") return;
        if (window.__PRERENDER__) return;
        if (navigator.userAgent.includes("HeadlessChrome")) return;

        // Missing credentials -> stay silent (don't break local dev or CI builds).
        if (!BOT_ID || !WEB_KEY) {
            if (env.DEV) {
                console.info(
                    "[NoovaWidget] Skipped: set VITE_NOOVA_BOT_ID and VITE_NOOVA_WEB_KEY in web/.env.local to enable the widget on the public site.",
                );
            }
            return;
        }

        if (hidden) {
            setWidgetVisible(false);
            return;
        }

        // Public route: make sure the widget is visible (clears any prior hide).
        setWidgetVisible(true);

        // Already injected -> nothing further to do.
        if (document.getElementById(SCRIPT_ID)) return;

        const s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.src = SCRIPT_URL;
        s.async = true;

        s.setAttribute("data-api-base", API_BASE);
        s.setAttribute("data-bot-id", BOT_ID);
        s.setAttribute("data-api-key", WEB_KEY);
        s.setAttribute("data-position", POSITION);
        s.setAttribute("data-surface", SURFACE);
        s.setAttribute("data-color-mode", COLOR_MODE);
        s.setAttribute("data-fab-variant", FAB_VARIANT);
        s.setAttribute("data-theme-preset", THEME_PRESET);
        // NOTE: no data-preview here — production embeds must not accept
        // runtime config over postMessage.

        document.body.appendChild(s);
    }, [hidden]);

    return null;
}
